import React, { useState, useEffect } from 'react'
import styles from './MarkdownViewer.module.css'
import { getHeaders } from '../../api'
import { renderMarkdownSafe } from '@lorefully/markdown'
import { Save24Regular } from '@fluentui/react-icons'

interface MarkdownViewerProps {
    src: string
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ src }) => {
    const [content, setContent] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<Error | null>(null)

    /**
     * Anchor links are not handled well by 'marked' and result in HTTP 404 errors as the URL they point to does not exist.
     * This function removes them from the resulted HTML.
     */
    const removeAnchorLinks = (html: string) => {
        const ancorLinksRegex =
            /<a\s+(?:[^>]*?\s+)?href=['"](#[^"']*?)['"][^>]*?>/g
        return html.replace(ancorLinksRegex, '')
    }

    useEffect(() => {
        const fetchMarkdown = async () => {
            try {
                const token = undefined
                const response = await fetch(src, {
                    method: 'GET',
                    headers: await getHeaders(token),
                })

                if (!response.ok) {
                    throw new Error('Failed loading markdown file.')
                }

                const markdownText = await response.text()
                // Sanitise first, then strip in-page anchor links (which marked
                // renders as #-hrefs that 404); the latter is cosmetic only.
                setContent(removeAnchorLinks(renderMarkdownSafe(markdownText)))
            } catch (error) {
                setError(error instanceof Error ? error : new Error(String(error)))
            } finally {
                setIsLoading(false)
            }
        }

        fetchMarkdown()
    }, [src])

    return (
        <div className={styles.markDownContainer}>
            {isLoading ? (
                <div className={`${styles.loading} ${styles.markdownViewer}`}>
                    <div className={styles.spinner} />
                    <p className={styles.loadingText}>Loading file</p>
                </div>
            ) : error ? (
                <div className={`${styles.error} ${styles.markdownViewer}`}>
                    <div className={styles.errorMessage}>
                        {error.message}
                        <a href={src} download className={styles.downloadLink}>
                            Download the file
                        </a>
                    </div>
                </div>
            ) : (
                <div className={styles.markdownViewer}>
                    <a
                        href={src}
                        download
                        className={styles.downloadButton}
                        title="Save"
                        aria-label="Save"
                    >
                        <Save24Regular />
                    </a>
                    <div
                        className={styles.markdown}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                </div>
            )}
        </div>
    )
}
